/* Swym's public storefront SDK only. Never place REST credentials in this asset. */
window.SimpsonHubFavorites = class {
  constructor(hub) {
    this.hub = hub;
    this.roots = [...hub.querySelectorAll('[data-favorites-live]')];
    this.listeners = new AbortController();
    this.revision = 0;
    this.items = new Map();
    this.lists = [];
    this.selected = '';
    this.dead = false;
    if (!this.roots.length) return;
    this.config = JSON.parse(this.roots[0].querySelector('[data-favorites-config]').textContent);
    this.sdkReady = new Promise(resolve => { this.resolveSdk = resolve; });
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(swat => {
      if (this.dead || this.swat) return;
      this.swat = swat;
      this.resolveSdk(swat);
      const layer = swat.evtLayer;
      if (layer?.addEventListener) {
        this.providerEvents = ['sw:addedtowishlist', 'sw:removedfromwishlist', swat.JSEvents?.customerInfoRefreshed || 'sw:customerInfoRefreshed'];
        this.providerListener = () => { this.clear(); this.activate(); };
        this.providerEvents.forEach(name => layer.addEventListener(name, this.providerListener));
      }
      this.activate();
    });
    hub.addEventListener('click', event => {
      if (event.target.closest('[data-hub-account-sign-out]')) { this.signedOut = true; this.clear(); return; }
      const root = event.target.closest('[data-favorites-live]');
      if (!root) return;
      if (event.target.closest('[data-favorites-retry], [data-favorites-refresh]')) this.load();
      if (event.target.closest('[data-favorites-more]')) this.loadPage(true);
      const remove = event.target.closest('[data-favorites-remove]');
      const cart = event.target.closest('[data-favorites-cart]');
      if (remove) this.remove(remove.dataset.favoritesRemove);
      if (cart) this.add(cart);
    }, {signal:this.listeners.signal});
    hub.addEventListener('change', event => {
      if (!event.target.matches('[data-favorites-list]') || this.busy) return;
      const lid = event.target.value;
      if (!this.lists.some(list => list.lid === lid)) return;
      this.selected = lid;
      this.loadPage();
    }, {signal:this.listeners.signal});
    window.addEventListener('pagehide', () => this.clear(), {signal:this.listeners.signal});
    window.addEventListener('pageshow', () => { this.clear(); this.activate(); }, {signal:this.listeners.signal});
  }
  visible() { return this.roots.some(root => !root.closest('[data-hub-view]')?.hidden) && this.hub.drawer?.open; }
  current() {
    if (this.dead || this.signedOut || this.hub.account?.loggingOut) return false;
    const loggedIn = this.swat?.platform?.isLoggedIn();
    if (this.hub.dataset.customerId) return !!this.config.email && loggedIn === true;
    return !this.config.email && loggedIn === false;
  }
  clear() {
    ++this.revision;
    this.loading = false; this.pageLoading = false; this.loadedAt = 0;
    this.request?.abort();
    this.items.clear(); this.lists = []; this.selected = ''; this.next = null; this.contents = null;
    this.busy = false;
    this.guestRegistration = null;
    this.render('unavailable');
  }
  activate() {
    if (!this.visible()) return;
    if (this.loadedAt && Date.now() - this.loadedAt < 60000 && this.current()) return;
    this.load();
  }
  diagnose(stage, error) {
    const known = ['timeout','identity','response'];
    const reason = known.includes(error?.message) ? error.message : error instanceof TypeError ? 'sdk-method-or-response-type' : 'provider-request-failed';
    const status = Number.isInteger(error?.status) ? error.status : null;
    // Never include emails, customer IDs, response bodies, URLs or credential values.
    this.lastFailure = {stage, reason, status, sdkReady:!!this.swat,
      authCheck:typeof this.swat?.authCheck === 'function',
      loginCheck:typeof this.swat?.platform?.isLoggedIn === 'function',
      fetchLists:typeof this.swat?.fetchLists === 'function',
      paginatedList:typeof this.swat?.wishlist?.fetchPaginatedList === 'function',
      listDetails:typeof this.swat?.fetchListDetails === 'function',
      responseShape:this.responseShape || null};
    this.roots.forEach(root => { root.dataset.favoritesFailure = `${stage}:${reason}`; });
    console.warn('[Simpson Favorites]', JSON.stringify(this.lastFailure));
  }
  call(invoke, timeout = 12000) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('timeout')), timeout);
      const finish = fn => result => { clearTimeout(timer); fn(result); };
      try { invoke(finish(resolve), finish(reject)); } catch (error) { clearTimeout(timer); reject(error); }
    });
  }
  async identity() {
    if (!this.current()) throw new Error('identity');
    const context = await this.call(ok => this.swat.authCheck(ok));
    if (!this.current()) throw new Error('identity');
    if (this.hub.dataset.customerId) {
      if (!context?.authn || typeof context.email !== 'string' || context.email.trim().toLowerCase() !== this.config.email.trim().toLowerCase()) throw new Error('identity');
      return;
    }
    if (context?.authn !== false || context.email) throw new Error('identity');
    // Guest auth responses can omit regid; the SDK keeps the active device registration.
    const deviceRegistration = this.swat.getSwymRegistrationId?.();
    const registration = context.regid || deviceRegistration;
    if (typeof registration !== 'string' || !registration) throw new Error('identity');
    if (context.regid && deviceRegistration && context.regid !== deviceRegistration) throw new Error('identity');
    if (this.guestRegistration && this.guestRegistration !== registration) throw new Error('identity');
    this.guestRegistration = registration;
  }
  async load() {
    if (!this.roots.length || this.dead || this.busy || this.loading || this.pageLoading) return;
    const revision = ++this.revision;
    this.loading = true;
    this.loadedAt = 0;
    this.responseShape = null;
    this.items.clear();
    this.guestRegistration = null;
    this.render('loading');
    if (this.signedOut || this.hub.account?.loggingOut) { this.loading = false; this.render('unavailable'); return; }
    let stage = 'sdk-ready';
    try {
      await this.call(ok => this.sdkReady.then(ok), 30000);
      if (revision !== this.revision || this.dead) return;
      stage = 'customer-identity';
      await this.identity();
      if (revision !== this.revision) return;
      stage = 'fetch-lists';
      const lists = await this.call((ok, fail) => this.swat.fetchLists({callbackFn:ok, errorFn:fail}));
      if (revision !== this.revision) return;
      stage = 'customer-identity-after-lists';
      await this.identity();
      if (revision !== this.revision) return;
      stage = 'list-response';
      this.responseShape = {array:Array.isArray(lists)};
      if (!Array.isArray(lists)) throw new Error('response');
      this.lists = lists.filter(list => typeof list.lid === 'string' && typeof list.lname === 'string' && (!list.lty || list.lty === 'wl'));
      this.selected = this.lists.some(list => list.lid === this.selected) ? this.selected : this.lists[0]?.lid || '';
      if (!this.selected) { this.loadedAt = Date.now(); this.render('empty'); return; }
      this.loading = false;
      await this.loadPage();
    } catch (error) {
      if (revision === this.revision) { this.diagnose(stage,error); this.items.clear(); this.render(error.message === 'identity' ? 'unavailable' : 'error'); }
    } finally {
      if (revision === this.revision) this.loading = false;
    }
  }
  async loadPage(append = false) {
    if (this.dead || this.busy || this.pageLoading || !this.selected) return;
    const revision = ++this.revision;
    this.pageLoading = true;
    this.loadedAt = 0;
    const lid = this.selected;
    this.request?.abort();
    this.request = new AbortController();
    if (!append) { this.items.clear(); this.next = null; }
    this.render('loading');
    let stage = 'customer-identity-before-items';
    try {
      await this.identity();
      if (revision !== this.revision) return;
      const offset = append ? this.next : 0;
      if (!Number.isSafeInteger(offset) || offset < 0) throw new Error('response');
      stage = 'fetch-list-details';
      const page = append && this.contents?.lid === lid ? this.contents : await this.call((ok, fail) => this.swat.fetchListDetails({lid}, ok, fail));
      if (revision !== this.revision) return;
      stage = 'customer-identity-after-items';
      await this.identity();
      if (revision !== this.revision) return;
      stage = 'item-response';
      this.responseShape = {listMatches:page?.list?.lid === lid,items:Array.isArray(page?.items)};
      if (page?.list?.lid !== lid || !Array.isArray(page.items)) throw new Error('response');
      this.contents = {...page,lid};
      // The native list-details API returns full contents. Hydrate/render only 50 per page.
      const raw = page.items.slice(offset,offset + 50).filter(item => !item.lid || item.lid === lid);
      const products = new Map();
      // Limit concurrent Shopify reads; a failed product never gets a speculative cart button.
      let cursor = 0;
      const results = new Array(raw.length);
      await Promise.all(Array.from({length:Math.min(4, raw.length)}, async () => {
        while (cursor < raw.length) {
          const index = cursor++;
          results[index] = await this.product(raw[index], products, this.request.signal);
        }
      }));
      stage = 'customer-identity-after-products';
      await this.identity();
      if (revision !== this.revision) return;
      results.forEach(item => { if (item) this.items.set(item.key, item); });
      this.next = offset + 50 < page.items.length ? offset + 50 : null;
      this.loadedAt = Date.now();
      this.render(this.items.size ? 'ready' : 'empty');
    } catch (error) {
      if (revision === this.revision) { this.diagnose(stage,error); this.items.clear(); this.render(error.message === 'identity' ? 'unavailable' : 'error'); }
    } finally {
      if (revision === this.revision) this.pageLoading = false;
    }
  }
  productURL(value) {
    try {
      const url = new URL(value, location.origin);
      const hosts = [location.hostname, this.config.store_domain];
      if (this.config.store_url) hosts.push(new URL(this.config.store_url).hostname);
      if (!hosts.includes(url.hostname) || !['http:', 'https:'].includes(url.protocol) || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) return null;
      // Saved canonical/myshopify URLs still use this storefront's current market and preview session.
      url.protocol = location.protocol; url.host = location.host;
      url.search = ''; url.hash = ''; return url;
    } catch (_) { return null; }
  }
  async product(raw, cache = new Map(), signal) {
    const epi = Number(raw.epi), empi = Number(raw.empi);
    if (!Number.isSafeInteger(epi) || epi <= 0 || !Number.isSafeInteger(empi) || empi <= 0) return null;
    const url = this.productURL(raw.du);
    const item = {key:`${empi}:${epi}`, raw, epi, empi, url, title:String(raw.dt || this.config.product_unavailable), variant:String(raw.vi || ''), available:false, price:null};
    if (!url) return item;
    try {
      if (!cache.has(url.href)) cache.set(url.href, fetch(`${url.href}.js`, {credentials:'same-origin', cache:'no-store', signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(12000)]) : AbortSignal.timeout(12000)}).then(response => { if (!response.ok) throw new Error('product'); return response.json(); }));
      const product = await cache.get(url.href);
      if (Number(product.id) !== empi) return item;
      const variant = product.variants?.find(variant => Number(variant.id) === epi);
      item.title = String(product.title || item.title);
      if (!variant) return item;
      item.variant = variant.public_title || variant.title || '';
      item.price = Number.isFinite(variant.price) ? variant.price : null;
      item.available = variant.available === true && item.price !== null;
      item.url.searchParams.set('variant', epi);
      const image = variant.featured_image?.src || product.featured_image;
      if (image) {
        const imageURL = new URL(image, location.origin);
        if (imageURL.protocol === 'https:' && (imageURL.origin === location.origin || imageURL.hostname === 'cdn.shopify.com')) item.image = imageURL.href;
      }
    } catch (_) { /* Preserve the saved item with removal and a product link. */ }
    return item;
  }
  async remove(key) {
    if (this.busy || !this.items.has(key)) return;
    const revision = this.revision, lid = this.selected, item = this.items.get(key);
    this.busy = true; this.lock(true);
    try {
      await this.identity();
      if (revision !== this.revision) return;
      await this.call((ok, fail) => this.swat.deleteFromList(lid, {epi:item.epi, empi:item.empi, du:item.raw.du}, ok, fail));
      await this.identity();
      if (revision !== this.revision) return;
      this.items.delete(key);
      this.render(this.items.size ? 'ready' : 'empty');
      this.status(this.config.removed);
      this.roots.find(root => !root.closest('[data-hub-view]')?.hidden)?.querySelector('[data-favorites-refresh]')?.focus();
    } catch (error) {
      if (revision === this.revision) {
        if (error.message === 'identity') this.clear();
        else this.status(this.config.remove_uncertain);
      }
    } finally { if (revision === this.revision) { this.busy = false; this.lock(false); } }
  }
  async add(button) {
    if (this.busy || this.hub.adding) return;
    const item = this.items.get(button.dataset.favoritesCart);
    if (!item) return;
    const revision = this.revision;
    this.busy = true; this.lock(true);
    try {
      await this.identity();
      const fresh = await this.product(item.raw);
      if (revision !== this.revision) return;
      await this.identity();
      if (revision !== this.revision) return;
      if (!fresh?.available) { this.status(this.config.product_unavailable); return; }
      // Reuse the hub's confirmed Shopify cart path, including uncertain-result handling.
      button.dataset.hubBuyAgain = fresh.epi;
      await this.hub.buyAgain(button);
    } catch (error) {
      if (revision === this.revision) { if (error.message === 'identity') this.clear(); else this.status(this.config.error_description); }
    } finally { delete button.dataset.hubBuyAgain; if (revision === this.revision) { this.busy = false; this.lock(false); } }
  }
  lock(value) { this.roots.forEach(root => root.querySelectorAll('button, select').forEach(control => { control.disabled = value; })); }
  status(value) { this.roots.forEach(root => { const status = root.querySelector('[data-favorites-status]'); if (status) status.textContent = value; }); }
  node(tag, className, text) { const node = document.createElement(tag); node.className = className; if (text !== undefined) node.textContent = text; return node; }
  button(text, attr, value = '') { const button = this.node('button','hub-favorites__button',text); button.type = 'button'; button.setAttribute(attr,value); return button; }
  render(state) {
    if (!this.config) return;
    this.roots.forEach(root => {
      root.dataset.state = state; root.setAttribute('aria-busy',String(state === 'loading'));
      const body = root.querySelector('[data-favorites-body]');
      body.replaceChildren();
      if (state === 'loading') { body.append(this.node('p','hub-favorites__status',this.config.loading)); return; }
      if (state === 'unavailable' || state === 'error') {
        const box = this.node('div','hub-favorites__message');
        box.append(this.node('h4','',this.config[`${state}_title`]),this.node('p','',this.config[`${state}_description`]));
        box.append(this.button(this.config.retry,'data-favorites-retry'));
        body.append(box); return;
      }
      const toolbar = this.node('div','hub-favorites__toolbar');
      if (this.lists.length) {
        const label = this.node('label','',this.config.list_label), select = this.node('select','');
        select.setAttribute('data-favorites-list','');
        this.lists.forEach(list => { const option = this.node('option','',list.lname); option.value = list.lid; option.selected = list.lid === this.selected; select.append(option); });
        label.append(select); toolbar.append(label);
      }
      const refresh = this.button(this.config.refresh,'data-favorites-refresh'); refresh.className = 'hub-favorites__text-button'; toolbar.append(refresh); body.append(toolbar);
      if (state === 'empty') {
        const box = this.node('div','hub-favorites__message'); box.append(this.node('h4','',this.config.empty_title),this.node('p','',this.config.empty_description));
        const browse = this.node('a','hub-favorites__button',this.config.browse); browse.href = this.config.browse_url; box.append(browse); body.append(box);
      } else {
        const list = this.node('ul','hub-favorites__items');
        this.items.forEach(item => {
          const row = this.node('li','hub-favorites__item'); row.setAttribute('data-favorites-item',item.key);
          const art = this.node('div','hub-favorites__image');
          if (item.image) { const image = this.node('img',''); image.src = item.image; image.alt = ''; image.width = 96; image.height = 112; image.loading = 'lazy'; art.append(image); }
          const copy = this.node('div','hub-favorites__copy');
          const title = this.node(item.url ? 'a' : 'strong','hub-favorites__title',item.title); if (item.url) title.href = item.url.href;
          copy.append(title,this.node('p','',item.variant));
          if (item.price !== null) copy.append(this.node('strong','',new Intl.NumberFormat(this.hub.dataset.locale, {style:'currency',currency:this.config.currency}).format(item.price / 100)));
          if (item.available) copy.append(this.button(this.config.add_to_cart,'data-favorites-cart',item.key));
          else { copy.append(this.node('span','hub-favorites__stock',this.config.product_unavailable)); if (item.url) { const link = this.node('a','hub-favorites__text-button',this.config.view_options); link.href = item.url.href; copy.append(link); } }
          const remove = this.button(this.config.remove,'data-favorites-remove',item.key); remove.className = 'hub-favorites__remove'; remove.setAttribute('aria-label',this.config.remove_label.replace('__PRODUCT_TITLE__',item.title)); copy.append(remove);
          const status = this.node('p','hub-favorites__status'); status.setAttribute('data-hub-add-status',''); status.setAttribute('role','status'); copy.append(status);
          row.append(art,copy); list.append(row);
        }); body.append(list);
      }
      if (this.next) body.append(this.button(this.config.load_more,'data-favorites-more'));
      const status = this.node('p','hub-favorites__status'); status.setAttribute('data-favorites-status',''); status.setAttribute('role','status'); status.setAttribute('aria-live','polite'); body.append(status);
    });
  }
  destroy() {
    this.dead = true; this.clear(); this.listeners.abort();
    if (this.swat?.evtLayer?.removeEventListener) this.providerEvents?.forEach(name => this.swat.evtLayer.removeEventListener(name,this.providerListener));
  }
};
